import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';
import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css'; // Tailwind inclus aici
import AppWrapper from './App';
import { BeneficiiProvider } from './components/cumtesimtiazi';
import { captureAttribution } from './utils/attribution';
import { logger } from './utils/logger';

// Sentry init — error monitoring
Sentry.init({
  dsn: 'https://7558b840e0f1c7d57fab7ea79665cc5e@o4511111512915968.ingest.de.sentry.io/4511111591297104',
  environment: process.env.NODE_ENV,
  // GDPR: nu trimite PII automat (IP, cookies, headers cu date personale).
  sendDefaultPii: false,
  integrations: [
    Sentry.browserTracingIntegration(),
  ],
  tracesSampleRate: 0.05,
  // Plasă de siguranță: elimină email/nume/IP chiar dacă apar accidental
  // în user context sau în `extra` la vreo captură de excepție.
  beforeSend(event) {
    if (event.user) {
      delete event.user.email;
      delete event.user.username;
      delete event.user.ip_address;
    }
    if (event.extra && typeof event.extra === 'object') {
      delete event.extra.email;
    }
    return event;
  },
});

// PostHog init — analytics
// Performanță WebView mobil: autocapture + pageleave dezactivate (overhead mare
// pe fiecare click/navigare). Pageview-urile le emitem manual la schimbarea de
// rută (vezi App.js). Identify-ul la login folosește doar UUID-ul (fără PII),
// iar evenimentele GAD-7 nu trimit scor/severitate (vezi authcontext / gad7).
posthog.init('phc_pdUMPsnarL1vnPpHgHPtngCdifX8gLczFfHWPVx6tMO', {
  api_host: 'https://eu.i.posthog.com',
  person_profiles: 'identified_only',
  capture_pageview: false,
  capture_pageleave: false,
  autocapture: false,
});

// Capture UTM params on first visit (first-touch attribution)
captureAttribution();

// După deploy, tab-uri deschise pot avea main.js vechi care cere chunk-uri șterse → ChunkLoadError.
// Reîncărcăm o singură dată ca să ia index.html + bundle-ul nou.
const isChunkLoadError = (reason) => {
  const message = reason?.message || String(reason || '');
  return /Loading chunk [\d]+ failed|ChunkLoadError|Failed to fetch dynamically imported module/i.test(message);
};

const reloadOnceOnStaleChunk = () => {
  const KEY = 'nativus_chunk_reload';
  const tryReload = (reason) => {
    if (!isChunkLoadError(reason)) return;
    if (sessionStorage.getItem(KEY)) return;
    sessionStorage.setItem(KEY, '1');
    window.location.reload();
  };
  window.addEventListener('unhandledrejection', (event) => tryReload(event.reason));
  window.addEventListener('error', (event) => tryReload(event.error || event.message));
};

// Track push notification opens (background clicks land with utm_medium=push_open)
if (window.location.search.includes('utm_medium=push_open')) {
  const params = new URLSearchParams(window.location.search);
  posthog.capture('notification_opened', {
    platform: 'web',
    type: 'background',
    campaign: params.get('utm_campaign') || 'push',
  });
}

// Înregistrează Service Worker pentru Firebase Messaging (doar web, nu native)
const isCapacitorNative = (() => {
  try { return require('@capacitor/core').Capacitor?.isNativePlatform() ?? false; } catch { return false; }
})();

if (!isCapacitorNative) {
  reloadOnceOnStaleChunk();
}

if (!isCapacitorNative && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/firebase-messaging-sw.js')
      .then((registration) => {
        logger.info('✅ Service Worker înregistrat cu succes:', registration.scope);
      })
      .catch((error) => {
        console.warn('⚠️ Eroare la înregistrarea Service Worker:', error);
      });
  });
}

const root = ReactDOM.createRoot(document.getElementById('root'));

root.render(
  <Sentry.ErrorBoundary fallback={<p style={{padding:'2rem',textAlign:'center'}}>A apărut o eroare. Te rugăm reîncarcă pagina.</p>}>
    <BeneficiiProvider>
      <AppWrapper />
    </BeneficiiProvider>
  </Sentry.ErrorBoundary>
);
